import * as Animate from "react-reveal";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "../../../components/svgs";
import { convertHTMLStringToHTML } from "../../../utils/convertHTMLStringToHTML";
import { getReadingTime } from "../../../utils/readingTime";

const SkeletonCard = () => (
  <div
    className="bg-white border border-[#e1ded5] rounded-2xl overflow-hidden grid animate-pulse min-h-[430px] lg:min-h-[470px]"
    style={{ gridTemplateRows: "210px 1fr" }}
  >
    <div className="bg-black/10 w-full h-full" />
    <div className="p-[22px] space-y-3">
      <div className="h-3 w-2/5 bg-black/10 rounded" />
      <div className="h-5 w-full bg-black/10 rounded mt-3" />
      <div className="h-5 w-4/5 bg-black/10 rounded" />
      <div className="h-3 w-full bg-black/10 rounded mt-3" />
      <div className="h-3 w-3/4 bg-black/10 rounded" />
    </div>
  </div>
);

const Loader = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[22px]">
    {Array.from({ length: 6 }).map((_, i) => (
      <SkeletonCard key={i} />
    ))}
  </div>
);

const BlogArticle = ({ blog }) => {
  const url = blog?.slug ? `/blog/${blog.slug}` : `/blog/${blog?._id}`;
  const excerpt =
    blog?.content?.length > 130
      ? blog.content.slice(0, 130) + "..."
      : blog?.content;

  return (
    <article
      className="group bg-white border border-[#e1ded5] rounded-2xl overflow-hidden grid transition-all duration-200 hover:-translate-y-[5px] hover:shadow-[0_20px_44px_#2520161f] min-h-[430px] lg:min-h-[470px]"
      style={{
        gridTemplateRows: "210px 1fr",
        boxShadow: "0 10px 28px #2520160f",
      }}
    >
      <Link
        to={url}
        className="relative block overflow-hidden"
        style={{ background: "#e7e8ed" }}
      >
        <img
          src={blog?.contentHeaderImage}
          alt=""
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[360ms] group-hover:scale-[1.035]"
        />
        {blog?.category && (
          <span
            className="absolute text-primary bg-secondary"
            style={{
              top: "15px",
              left: "15px",
              letterSpacing: "0.05em",
              borderRadius: "6px",
              padding: "7px 9px",
              fontSize: "15px",
              fontWeight: 800,
            }}
          >
            {blog.category.toUpperCase()}
          </span>
        )}
      </Link>

      <div className="flex flex-col items-start" style={{ padding: "22px" }}>
        <div
          className="flex justify-between items-center w-full"
          style={{
            gap: "15px",
            color: "#777b84",
            fontSize: "15px",
            fontWeight: 700,
          }}
        >
          <span style={{ color: "#8a5900" }}>{blog?.category}</span>
          <span>{getReadingTime(blog?.content)} min read</span>
        </div>
        <h3
          className="text-primary"
          style={{
            letterSpacing: "-0.025em",
            margin: "14px 0 12px",
            fontFamily: "Georgia, serif",
            fontSize: "25px",
            fontWeight: 600,
            lineHeight: 1.18,
          }}
        >
          <Link to={url} style={{ color: "inherit" }}>
            {blog?.title}
          </Link>
        </h3>
        <p
          style={{
            color: "#5e6574",
            margin: "0 0 19px",
            fontSize: "15px",
            lineHeight: 1.62,
          }}
          dangerouslySetInnerHTML={convertHTMLStringToHTML(excerpt)}
        />
        <Link
          to={url}
          className="text-primary inline-flex items-center"
          style={{
            borderBottom: "1px solid var(--color-primary)",
            gap: "8px",
            marginTop: "auto",
            paddingBottom: "3px",
            fontSize: "15px",
            fontWeight: 800,
          }}
        >
          Read article <ArrowUpRight className="w-3 h-3" />
        </Link>
      </div>
    </article>
  );
};

const BlogArticles = ({ loading, blogs = [] }) => {
  if (loading) return <Loader />;

  if (blogs?.length === 0) {
    return (
      <div
        className="text-center grid place-items-center"
        style={{
          background: "#fff9ed",
          border: "1px dashed #c9c5ba",
          borderRadius: "18px",
          minHeight: "340px",
          padding: "44px 24px",
        }}
      >
        <span
          className="text-secondary block"
          style={{ fontSize: "45px", transform: "rotate(-15deg)" }}
        >
          ⌕
        </span>
        <h3
          className="text-primary"
          style={{
            margin: "7px 0",
            fontFamily: "Georgia, serif",
            fontSize: "29px",
          }}
        >
          No articles yet
        </h3>
        <p style={{ color: "#5e6574", margin: 0, fontSize: "15px" }}>
          Check back soon for new stories and guides.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[22px]">
      {blogs?.map((blog, index) => (
        <Animate.Fade bottom key={blog?._id || index}>
          <BlogArticle blog={blog} />
        </Animate.Fade>
      ))}
    </div>
  );
};

export default BlogArticles;
